// The record page's History section (MOTHER-83): what loads when, and what
// "Show more" does. Free of React, so its paging and races are tested by
// driving it directly (historyLoader.test.ts); useRecordHistory only connects
// it to the record page.
import type { ObjectKey } from '../navigation';
import { appendPage, type Fetch, fetchHistory, HISTORY_STEP, nextStep, type RecordHistory } from './historyApi';

/** Which record a history belongs to. */
export const historyKey = (object: ObjectKey, recordId: string): string => `${object}:${recordId}`;

/** A record's history, and which record it is. */
export interface HistorySnapshot {
  key: string | null;
  history: RecordHistory;
}

const LOADING: RecordHistory = { state: 'loading' };

/**
 * The history to show for `object`/`recordId`: another record's never shows
 * under this one, even for the moment before the loader hears of the move.
 */
export function historyFor(snapshot: HistorySnapshot, object: ObjectKey, recordId: string): RecordHistory {
  return snapshot.key === historyKey(object, recordId) ? snapshot.history : LOADING;
}

/**
 * Loads a record's history, newest first, and pages through it. The record
 * page calls `load` once the record has loaded, and again whenever it changes:
 * a save or a Path step that adds an entry then shows it. While the same
 * record reloads, the entries already shown stay; another record starts from
 * nothing. `abandon` (the record reloaded, changed or went away) drops what is
 * still loading, so a "Show more" page never lands on a fresh first page or on
 * another record.
 */
export class HistoryLoader {
  private snapshot: HistorySnapshot = { key: null, history: LOADING };
  private readonly listeners = new Set<() => void>();
  private fetcher: Fetch | null = null;
  private object: ObjectKey | null = null;
  private recordId = '';
  /** Bumped by every load and abandon; a request answering for an older one is ignored. */
  private generation = 0;
  private pending = new Set<AbortController>();

  /** For useSyncExternalStore: called after every change. */
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** The current history and its record; a new object after every change. */
  readonly getSnapshot = (): HistorySnapshot => this.snapshot;

  private set(history: RecordHistory, key: string | null = this.snapshot.key): void {
    this.snapshot = { key, history };
    for (const listener of this.listeners) listener();
  }

  /** Drops everything still loading. */
  readonly abandon = (): void => {
    this.generation += 1;
    for (const controller of this.pending) controller.abort();
    this.pending.clear();
  };

  /** Starts a request that counts only while no load or abandon has come since. */
  private request(before?: string): { live: () => boolean; done: Promise<Awaited<ReturnType<typeof fetchHistory>>> } {
    const controller = new AbortController();
    const run = this.generation;
    this.pending.add(controller);
    const live = () => !controller.signal.aborted && run === this.generation;
    const done = fetchHistory(this.fetcher!, this.object!, this.recordId, { ...(before === undefined ? {} : { before }), signal: controller.signal });
    const settle = () => {
      this.pending.delete(controller);
    };
    done.then(settle, settle);
    return { live, done };
  }

  /**
   * The record page shows `object`/`recordId`; `loaded` is false until its
   * record has loaded, and nothing is asked for until then. Called again with
   * the same record each time it reloads.
   */
  load(fetcher: Fetch, object: ObjectKey, recordId: string, loaded: boolean): void {
    this.abandon();
    const key = historyKey(object, recordId);
    this.fetcher = fetcher;
    this.object = object;
    this.recordId = recordId;
    if (this.snapshot.key !== key) this.set(LOADING, key);
    if (!loaded) return;
    const { live, done } = this.request();
    done
      .then((page) => {
        if (!live()) return;
        // A "Show more" started while this page loaded belongs to the entries it replaces.
        this.abandon();
        if (!page || page.entries.length === 0) this.set({ state: 'hidden' });
        else this.set({ state: 'ready', entries: page.entries, hasMore: page.hasMore, shown: HISTORY_STEP, moreBusy: false, moreFailed: false });
      })
      .catch(() => {
        if (live()) this.set({ state: 'error' });
      });
  }

  /** "Show more": the next 10 already loaded, else the page before the last loaded entry. */
  readonly showMore = (): void => {
    const history = this.snapshot.history;
    if (history.state !== 'ready' || history.moreBusy) return;
    const step = nextStep(history.entries.length, history.shown, history.hasMore);
    if (step === 'none') return;
    if (step === 'reveal') {
      this.set({ ...history, shown: history.shown + HISTORY_STEP, moreFailed: false });
      return;
    }
    const last = history.entries[history.entries.length - 1];
    if (!last) return;
    this.set({ ...history, moreBusy: true, moreFailed: false });
    const { live, done } = this.request(last.changedAt);
    done
      .then((page) => {
        const current = this.snapshot.history;
        if (!live() || current.state !== 'ready') return;
        this.set({
          ...current,
          entries: appendPage(current.entries, page?.entries ?? []),
          hasMore: page?.hasMore ?? false,
          shown: current.shown + HISTORY_STEP,
          moreBusy: false,
        });
      })
      .catch(() => {
        const current = this.snapshot.history;
        if (live() && current.state === 'ready') this.set({ ...current, moreBusy: false, moreFailed: true });
      });
  };
}
